import React, { useCallback, useRef, useState, useEffect } from 'react';
import { IconDocumentNew, IconNext, IconPrev, IconRotate, IconZoomIn, IconZoomOut } from 'components/Icons';
import Loading from 'components/Loading';
import DownloadButton from '../../../components/Attachments/DownloadButton';

type ImageViewerProps = {
  images: string[];
  fileNameRelated: (undefined | string | null)[];
};

type DisplayMode = 'image' | 'iframe' | 'download' | 'csv';

type ImageState = {
  displayMode: DisplayMode;
  loadFailed: boolean;
  iframeFailed: boolean;
  contentType?: string;
  typeDetected: boolean;
};

const steps = [0.5, 0.75, 1, 1.5, 2, 3, 4];

function findNearestIndex(arr: number[], num: number) {
  let nearestIndex = 0;
  let minDifference = Math.abs(arr[0] - num);

  for (let i = 1; i < arr.length; i++) {
    const currentDifference = Math.abs(arr[i] - num);
    if (currentDifference < minDifference) {
      nearestIndex = i;
      minDifference = currentDifference;
    }
  }
  return nearestIndex;
}

// Helper function to get file extension
const getFileExtension = (filename: string): string => {
  const match = /\.([0-9a-z]+)(?:[?#]|$)/i.exec(filename);
  return match ? match[1] : '';
};

const ImageViewer = ({ images, fileNameRelated }: ImageViewerProps) => {
  const [loading, setLoading] = useState<boolean>(true);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const [isDragging, setIsDragging] = useState<boolean>(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [scaleIndex, setScaleIndex] = useState(2);
  const [rotation, setRotation] = useState(0);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imagePosition, setImagePosition] = useState({ left: 0, top: 0 });

  const [scale, setScale] = useState(steps[scaleIndex]);

  // Determine display mode based on content type
  const getDisplayModeFromContentType = (contentType: string | undefined): DisplayMode => {
    if (!contentType) return 'image'; // Default to image, will fallback if fails
    const type = contentType.toLowerCase();

    if (type.includes('csv') || type === 'text/csv' || type === 'application/csv') {
      return 'csv';
    }
    if (type.startsWith('image/')) {
      return 'image';
    }
    if (type === 'application/pdf') {
      return 'iframe';
    }
    // For other document types, show download (iframe won't work with Content-Disposition: attachment)
    return 'download';
  };

  // Determine initial display mode based on file extension (fallback)
  const getInitialDisplayMode = (url: string): DisplayMode => {
    const extension = getFileExtension(url).toLowerCase();
    if (extension === 'csv') return 'csv';
    if (extension === 'pdf') return 'iframe';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(extension)) return 'image';
    if (['doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt'].includes(extension)) return 'download';
    // Unknown extension - default to image, will detect via HEAD request
    return 'image';
  };

  // Track display mode for each image (image or iframe or download or csv)
  const [imageStates, setImageStates] = useState<ImageState[]>(() =>
    images.map(url => ({
      displayMode: getInitialDisplayMode(url),
      loadFailed: false,
      iframeFailed: false,
      typeDetected: getFileExtension(url) !== '', // Already detected if has extension
    }))
  );

  // Detect content type via HEAD request for URLs without extensions
  useEffect(() => {
    images.forEach((url, index) => {
      const state = imageStates[index];
      if (state && !state.typeDetected) {
        fetch(url, { method: 'HEAD' })
          .then(response => {
            const contentType = response.headers.get('Content-Type') || '';
            const displayMode = getDisplayModeFromContentType(contentType);
            setImageStates(prevStates =>
              prevStates.map((s, idx) => (idx === index ? { ...s, contentType, displayMode, typeDetected: true } : s))
            );
          })
          .catch(() => {
            // If HEAD fails, mark as detected and keep default (will try image then fallback)
            setImageStates(prevStates =>
              prevStates.map((s, idx) => (idx === index ? { ...s, typeDetected: true } : s))
            );
          });
      }
    });
  }, [images]);

  // Update imageStates when images prop changes
  useEffect(() => {
    setImageStates(prevStates => {
      if (prevStates.length === images.length) {
        return prevStates; // Keep existing states
      }

      // Initialize new states for any new images
      return images.map((url, index) =>
        index < prevStates.length
          ? prevStates[index]
          : {
              displayMode: getInitialDisplayMode(url),
              loadFailed: false,
              iframeFailed: false,
              typeDetected: getFileExtension(url) !== '',
            }
      );
    });
  }, [images]);

  // Reset view parameters when current index changes
  useEffect(() => {
    const currentState = imageStates[currentIndex];
    // Only show loading for image and iframe modes
    if (currentState?.displayMode === 'image' || currentState?.displayMode === 'iframe') {
      setLoading(true);
    } else {
      // CSV and download modes don't need loading
      setLoading(false);
    }
  }, [currentIndex, imageStates[currentIndex]?.displayMode]);

  const markIframeFailed = () => {
    setImageStates(prevStates =>
      prevStates.map((state, idx) =>
        idx === currentIndex
          ? {
              ...state,
              iframeFailed: true,
              displayMode: 'download',
            }
          : state
      )
    );
    // Make sure loading is set to false when marking as failed
    setLoading(false);
  };

  const handleZoomIn = useCallback(() => {
    if (scaleIndex >= steps.length - 1) return;
    setScaleIndex(scaleIndex + 1);
    setScale(steps[scaleIndex + 1]);
  }, [scaleIndex]);

  const handleZoomOut = useCallback(() => {
    if (scaleIndex <= 0) return;
    setScaleIndex(scaleIndex - 1);
    setScale(steps[scaleIndex - 1]);
  }, [scaleIndex]);

  const resetZoom = () => {
    calculateFitAndCenter();
  };

  const handleRotateCounterClockwise = () => {
    setRotation(rotation - 90);
  };

  const handleThumbnailClick = (index: number) => {
    setCurrentIndex(index);
    // Don't reset zoom here as it will be handled by the useEffect
  };

  const handleMoveDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    setDragStart({ x: e.nativeEvent.clientX, y: e.nativeEvent.clientY });
  };

  const handleMove = (e: React.MouseEvent) => {
    const { clientX, clientY } = e.nativeEvent;

    if (isDragging) {
      const offsetX = clientX - dragStart.x;
      const offsetY = clientY - dragStart.y;

      setImagePosition(prev => ({
        left: prev.left + offsetX,
        top: prev.top + offsetY,
      }));

      setDragStart({ x: clientX, y: clientY });
    }
  };

  const handleMoveUp = (e: React.MouseEvent) => {
    setIsDragging(false);
  };

  const renderThumbnails = () => {
    return images.map((image, index) => {
      const state = imageStates[index];
      const isNonImageDocument =
        state?.displayMode === 'iframe' || state?.displayMode === 'download' || state?.displayMode === 'csv';

      if (isNonImageDocument) {
        // For iframe or download types, use a simpler document icon that matches the existing UI
        return (
          <div
            key={index}
            className={`thumbnail-container ${currentIndex === index ? 'active' : ''}`}
            onClick={() => handleThumbnailClick(index)}
          >
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
              }}
            >
              {/* Simple document icon */}
              <IconDocumentNew />
            </div>
          </div>
        );
      } else {
        // Regular image thumbnail
        return (
          <div key={index} className={`thumbnail-container ${currentIndex === index ? 'active' : ''}`}>
            <img
              src={image}
              alt={`Thumbnail ${index + 1}`}
              className={`thumbnail ${currentIndex === index ? 'active' : ''}`}
              onClick={() => handleThumbnailClick(index)}
            />
          </div>
        );
      }
    });
  };

  const showActionButtons = images.length > 1;

  const handlePrev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const handleNext = () => {
    if (currentIndex < images.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const calculateFitAndCenter = (currentRotation: number = rotation) => {
    if (canvasRef?.current && imageRef?.current) {
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const canvasWidth = canvasRect.width;
      const canvasHeight = canvasRect.height;
      const naturalWidth = imageRef.current.naturalWidth || imageRef.current.width;
      const naturalHeight = imageRef.current.naturalHeight || imageRef.current.height;

      if (naturalWidth === 0 || naturalHeight === 0 || canvasWidth === 0 || canvasHeight === 0) {
        return;
      }

      // Account for rotation - swap dimensions for 90 and 270 degree rotations
      const normalizedRotation = ((currentRotation % 360) + 360) % 360;
      const isRotated90or270 = normalizedRotation === 90 || normalizedRotation === 270;
      const effectiveWidth = isRotated90or270 ? naturalHeight : naturalWidth;
      const effectiveHeight = isRotated90or270 ? naturalWidth : naturalHeight;

      const scaleX = canvasWidth / effectiveWidth;
      const scaleY = canvasHeight / effectiveHeight;
      // Use min to fit entire image, then snap to nearest step
      const calculatedScale = Math.min(scaleX, scaleY);
      const index = findNearestIndex(steps, calculatedScale);
      // Use the actual step value for consistent zoom behavior
      const fitScale = steps[index];

      setScale(fitScale);
      setScaleIndex(index);

      // Center the image in the canvas (accounting for rotation)
      const scaledWidth = effectiveWidth * fitScale;
      const scaledHeight = effectiveHeight * fitScale;
      const centerX = (canvasWidth - scaledWidth) / 2;
      const centerY = (canvasHeight - scaledHeight) / 2;

      // Adjust position based on rotation (since transformOrigin is top-left)
      let left = centerX;
      let top = centerY;

      if (normalizedRotation === 90) {
        left = centerX + scaledWidth;
      } else if (normalizedRotation === 180) {
        left = centerX + scaledWidth;
        top = centerY + scaledHeight;
      } else if (normalizedRotation === 270) {
        top = centerY + scaledHeight;
      }

      setImagePosition({ left, top });
    }
  };

  const handleImageLoaded = (event: React.SyntheticEvent<HTMLImageElement>) => {
    // Use requestAnimationFrame to ensure layout is complete before calculating fit
    requestAnimationFrame(() => {
      calculateFitAndCenter();
      setLoading(false);
    });
  };

  const handleImageError = () => {
    setLoading(false);
    // Mark the current image as failed and try iframe mode
    setImageStates(prevStates =>
      prevStates.map((state, idx) =>
        idx === currentIndex ? { ...state, loadFailed: true, displayMode: 'iframe' } : state
      )
    );
  };

  // Determine current display mode
  const getCurrentDisplayMode = (): DisplayMode => {
    const currentState = imageStates[currentIndex];
    if (!currentState) return getInitialDisplayMode(images[currentIndex]);

    // Simply respect the current display mode stored in state
    // This avoids logic that might inadvertently switch modes
    return currentState.displayMode;
  };

  const currentDisplayMode = getCurrentDisplayMode();
  const currentState = imageStates[currentIndex];

  // Navigation arrows JSX
  const navigationArrows = (
    <>
      <div className="nav-arrow left">
        {showActionButtons && (
          <button onClick={handlePrev} disabled={currentIndex === 0}>
            <IconPrev />
          </button>
        )}
      </div>
      <div className="nav-arrow right">
        {showActionButtons && (
          <button onClick={handleNext} disabled={currentIndex === images.length - 1}>
            <IconNext />
          </button>
        )}
      </div>
    </>
  );

  // Thumbnails JSX
  const thumbnails = images.length > 1 ? <div className="thumbnails">{renderThumbnails()}</div> : null;

  const handleTryAgainClick = () => {
    setImageStates(prevStates =>
      prevStates.map((state, idx) =>
        idx === currentIndex
          ? {
              ...state,
              displayMode: getInitialDisplayMode(images[currentIndex]),
              loadFailed: false,
              iframeFailed: false,
            }
          : state
      )
    );
    setLoading(true);
  };

  const iframeStyles: React.CSSProperties = {
    pointerEvents: 'all',
    display: loading ? 'none' : 'block',
    width: '100%',
    height: '100%',
    border: 'none',
  };

  // Render appropriate content based on display mode
  const renderCanvasContent = () => {
    // Show loading while detecting content type
    if (currentState && !currentState.typeDetected) {
      return (
        <div className="canvas">
          <div className="file-preview-content">
            <Loading />
          </div>
        </div>
      );
    }

    switch (currentDisplayMode) {
      case 'download':
        return (
          <div className="canvas">
            <div className="file-preview-content">
              <p>The document could not be displayed at the moment.</p>
              <DownloadButton
                fileNameRelated={fileNameRelated ? [fileNameRelated[currentIndex]] : undefined}
                attachmentsUrl={[images[currentIndex]]}
              />
              <button className="try-again-btn" onClick={handleTryAgainClick}>
                Try Again
              </button>
            </div>
          </div>
        );

      case 'iframe':
        return (
          <div className="canvas iframe-canvas">
            {loading && (
              <div className="file-preview-content">
                <Loading />
              </div>
            )}
            <iframe
              ref={iframeRef}
              style={iframeStyles}
              src={images[currentIndex]}
              title={`Content ${currentIndex + 1}`}
              onLoad={() => {
                setLoading(false);
                setImageStates(prevStates =>
                  prevStates.map((state, idx) => (idx === currentIndex ? { ...state, iframeFailed: false } : state))
                );
              }}
              onError={() => {
                markIframeFailed();
              }}
            />
          </div>
        );

      case 'csv':
        return (
          <div className="canvas">
            <div className="file-preview-content">
              <IconDocumentNew />
              <p style={{ marginTop: '16px', marginBottom: '16px' }}>CSV File</p>
              <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
                Preview not available. Click below to download.
              </p>
              <DownloadButton
                fileNameRelated={fileNameRelated ? [fileNameRelated[currentIndex]] : undefined}
                attachmentsUrl={[images[currentIndex]]}
              />
            </div>
          </div>
        );

      case 'image':
      default:
        return (
          <div className="canvas" ref={canvasRef}>
            <div
              className="image-container"
              onMouseDown={handleMoveDown}
              onMouseMove={handleMove}
              onMouseUp={handleMoveUp}
            >
              {loading && <Loading />}
              <img
                ref={imageRef}
                onLoadStart={() => setLoading(true)}
                onLoad={handleImageLoaded}
                onError={handleImageError}
                src={images[currentIndex]}
                alt="Zoomable Rotatable"
                style={{
                  left: imagePosition.left,
                  top: imagePosition.top,
                  transform: `scale(${scale}) rotate(${rotation}deg)`,
                  transformOrigin: 'top left',
                  opacity: loading ? 0 : 1,
                }}
              />
            </div>
          </div>
        );
    }
  };

  // Control bar only shown for image mode
  const controlBar =
    currentDisplayMode === 'image' ? (
      <div className="control-bar">
        <div className="zoom-controls">
          <button onClick={handleZoomIn}>
            <IconZoomIn />
          </button>
          <button className="btn-fit" onClick={resetZoom}>
            FIT
          </button>
          <button onClick={handleZoomOut}>
            <IconZoomOut />
          </button>
        </div>
        <div className="rotate-controls">
          <button onClick={handleRotateCounterClockwise}>
            <IconRotate />
          </button>
        </div>
      </div>
    ) : null;

  return (
    <div className="image-viewer-container">
      {controlBar}
      {navigationArrows}
      {renderCanvasContent()}
      {thumbnails}
    </div>
  );
};

export default ImageViewer;
